import { ChangeDetectionStrategy, Component, Directive, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

export interface SeparatorInputData {
  value: string;
  separator: ',' | ' ';
  byEntry?: boolean;
}

@Directive({
  selector: 'app-separator-label',
  standalone: false,
})
export class SeparatorInputLabelDirective {
}

@Component({
  selector: 'app-separator-input',
  templateUrl: './separator-input.component.html',
  styleUrls: ['./separator-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SeparatorInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SeparatorInputComponent implements OnInit, ControlValueAccessor {
  readonly form = new UntypedFormGroup({
    value: new UntypedFormControl(),
    separator: new UntypedFormControl(),
    byEntry: new UntypedFormControl(),
  });

  private _separatorToggle = true;
  private _entryToggle = true;

  @Input()
  byEntry = false;

  get separator(): string {
    return this._separatorToggle ? ' ' : ',';
  }

  get separatorLabel(): string {
    return this._separatorToggle ? '_' : ',';
  }

  get labelSuffix(): string {
    return this.translateService.instant(this._separatorToggle ? 'CONTROLS.space_separated' : 'CONTROLS.comma_separated');
  }

  get containsIcon(): string {
    return this.entryToggle ? 'read_more' : 'drag_handle';
  }

  get containsTooltip(): string {
    return this.translateService.instant(this.entryToggle ? 'CONTROLS.contains' : 'CONTROLS.equals');
  }

  get entryToggle(): boolean {
    return this._entryToggle;
  }

  set entryToggle(value: boolean) {
    this._entryToggle = value;
  }

  constructor(private translateService: TranslateService) { }

  ngOnInit(): void {
    this.form.valueChanges.subscribe(() => this.notify());
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  writeValue(value?: SeparatorInputData): void {
    this._separatorToggle = value?.separator === ' ';
    this._entryToggle = value?.byEntry ?? true;

    this.form.setValue({
      value: value?.value || '',
      separator: value?.separator ?? ' ',
      byEntry: value?.byEntry ?? true,
    });
  }

  toggleSeparator(): void {
    this._separatorToggle = !this._separatorToggle;
    this.form.patchValue({ separator: this.separator });
  }

  toggleEntry(): void {
    this._entryToggle = !this._entryToggle;
    this.form.patchValue({ byEntry: this.entryToggle });
  }

  private notify(): void {
    this._onChange(this.form.value);
    this._onTouched();
    // console.log(this.form.value);
  }

  private _onChange = (_: any) => {};
  private _onTouched = () => {};
}
